import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatCpf } from '../common/validators';

interface Termo {
  id: number;
  id_aluno: number;
  tipo: string;
  titulo: string;
  conteudo?: string | null;
  status: string;
  assinante_nome?: string | null;
  assinante_cpf?: string | null;
  assinado_em?: string | null;
  ip_assinatura?: string | null;
  modelo_aprovado?: boolean;
  aluno?: { nome?: string } | null;
}

// Aviso jurídico mostrado ENQUANTO o modelo não foi aprovado pela creche. Some do
// documento oficial (aprovado/assinado). Espelha o texto do backend.
const AVISO_MODELO = [
  '⚠️  MODELO SUJEITO À APROVAÇÃO DO DEPARTAMENTO JURÍDICO DA CRECHE',
  '',
  'Este documento é um MODELO oferecido pelo sistema apenas como ponto de partida.',
  'Sua adoção pela creche depende de análise e APROVAÇÃO PRÉVIA do departamento',
  'jurídico da instituição, que deverá adequá-lo à legislação vigente e à realidade',
  'da creche. O sistema não presta consultoria jurídica e não se responsabiliza pelo',
  'uso deste modelo sem a devida revisão.',
].join('\n');

/** Remove um bloco de aviso legado que ficou embutido no início do texto (termos antigos). */
const semAvisoLegado = (txt?: string | null) =>
  String(txt ?? '').replace(/={10,}[\s\S]*?MODELO SUJEIT[\s\S]*?={10,}\s*/i, '').replace(/^\s+/, '');
interface Aluno { id: number; nome: string; responsavel_nome?: string | null; responsavel_cpf?: string | null }

const EMERALD = '#10b981';
const TIPOS: { value: string; label: string }[] = [
  { value: 'contrato_matricula', label: 'Contrato de matrícula' },
  { value: 'uso_imagem', label: 'Autorização de uso de imagem' },
  { value: 'saida_pedagogica', label: 'Autorização de saída pedagógica' },
  { value: 'autorizacao_medicamento', label: 'Autorização de medicamento' },
  { value: 'lgpd_dados', label: 'Consentimento LGPD (dados)' },
  { value: 'outro', label: 'Outro' },
];
const labelTipo = (v: string) => TIPOS.find((t) => t.value === v)?.label || v;

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 };
const btn = (bg: string): React.CSSProperties => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 });
const inp: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%' };
const statusColor: Record<string, [string, string]> = {
  assinado: ['#d1fae5', '#065f46'], pendente: ['#fef9c3', '#854d0e'],
  recusado: ['#fee2e2', '#991b1b'], revogado: ['#f1f5f9', '#475569'],
};

export default function SecretariaPage() {
  const [termos, setTermos] = useState<Termo[]>([]);
  const [alunos, setAlunos] = useState<Aluno[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [novo, setNovo] = useState({ id_aluno: 0, tipo: 'contrato_matricula', titulo: '' });
  const [assinando, setAssinando] = useState<Termo | null>(null);
  const [assinatura, setAssinatura] = useState({ assinante_nome: '', assinante_cpf: '' });
  const [editandoTexto, setEditandoTexto] = useState<Termo | null>(null);
  const [textoEdit, setTextoEdit] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        api.get('/secretaria/termos', { params: filtroStatus ? { status: filtroStatus } : {} }),
        api.get('/alunos'),
      ]);
      setTermos(Array.isArray(t.data) ? t.data : []);
      const al = Array.isArray(a.data) ? a.data : a.data?.data || [];
      setAlunos(al);
    } catch {
      setMsg({ tipo: 'erro', texto: 'Falha ao carregar termos.' });
    } finally { setLoading(false); }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  async function acao(fn: () => Promise<any>, ok: string) {
    try { await fn(); setMsg({ tipo: 'ok', texto: ok }); await carregar(); }
    catch (e: any) { setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Operação falhou.' }); }
  }

  async function criar() {
    if (!novo.id_aluno) return setMsg({ tipo: 'erro', texto: 'Selecione a criança.' });
    if (novo.tipo === 'outro') {
      if (!novo.titulo.trim()) return setMsg({ tipo: 'erro', texto: 'Para "Outro", informe o título do termo.' });
      await acao(() => api.post('/secretaria/termos', novo), 'Termo criado.');
    } else {
      // Gera o MODELO já preenchido (com dados da criança/responsável/creche + alerta jurídico).
      await acao(
        () => api.post(`/alunos/${novo.id_aluno}/gerar-documento`, { tipo: novo.tipo }),
        'Modelo gerado — revise o conteúdo em Termos abaixo antes de adotar.',
      );
    }
    setNovo({ id_aluno: 0, tipo: 'contrato_matricula', titulo: '' });
  }

  /**
   * Abre o modal de assinatura já com o assinante PREENCHIDO a partir do
   * responsável legal da criança (o termo é gerado para uma criança definida —
   * o assinante é o mesmo responsável da matrícula). Sem digitação manual.
   */
  function abrirAssinatura(t: Termo) {
    const aluno = alunos.find((a) => a.id === t.id_aluno);
    setAssinatura({
      assinante_nome: t.assinante_nome || aluno?.responsavel_nome || '',
      assinante_cpf: aluno?.responsavel_cpf ? formatCpf(aluno.responsavel_cpf) : '',
    });
    setAssinando(t);
  }

  /**
   * Abre o documento do termo para VER / IMPRIMIR / salvar em PDF. Busca o termo
   * completo (conteúdo + assinatura) e monta uma página imprimível com o bloco de
   * assinatura eletrônica (quem/CPF/data-hora/IP) quando já assinado.
   */
  async function verDocumento(t: Termo) {
    let termo = t;
    try {
      const { data } = await api.get<Termo>(`/secretaria/termos/${t.id}`);
      if (data) termo = data;
    } catch { /* usa o que já tem na lista */ }

    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const dataHora = (s?: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '');
    const assinado = termo.status === 'assinado';
    const blocoAssinatura = assinado
      ? `<div class="assinatura">
           <h3>Assinatura eletrônica</h3>
           <p>Assinado eletronicamente por <strong>${esc(termo.assinante_nome)}</strong>${termo.assinante_cpf ? `, CPF ${esc(termo.assinante_cpf)}` : ''}, em <strong>${esc(dataHora(termo.assinado_em))}</strong>${termo.ip_assinatura ? `, a partir do IP ${esc(termo.ip_assinatura)}` : ''}.</p>
           <p class="obs">Aceite eletrônico registrado pelo sistema, com data, hora e IP, para fins de comprovação (assinatura eletrônica — Lei nº 14.063/2020 e MP 2.200-2/2001).</p>
         </div>`
      : `<div class="assinatura pendente">Documento ainda NÃO assinado (situação: ${esc(termo.status)}).</div>`;

    const win = window.open('', '_blank');
    if (!win) { setMsg({ tipo: 'erro', texto: 'Permita pop-ups para visualizar/imprimir o documento.' }); return; }
    // Aviso jurídico só enquanto NÃO aprovado. Documento oficial (aprovado/assinado) sai limpo.
    const avisoHtml = !termo.modelo_aprovado
      ? `<div class="aviso">${esc(AVISO_MODELO)}</div>`
      : '';
    win.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0"><title>${esc(termo.titulo)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;margin:0;padding:28px;}
        .doc{max-width:800px;margin:0 auto;}
        h1{font-size:18px;color:#0f172a;margin:0 0 14px;}
        pre{white-space:pre-wrap;font-family:inherit;font-size:13px;line-height:1.5;color:#334155;}
        .aviso{white-space:pre-wrap;background:#fffbeb;border:1px solid #fde68a;color:#92400e;border-radius:8px;padding:12px 14px;font-size:12px;margin-bottom:18px;}
        .assinatura{margin-top:26px;border-top:2px solid #e2e8f0;padding-top:14px;font-size:13px;}
        .assinatura h3{font-size:14px;color:#065f46;margin:0 0 6px;}
        .assinatura.pendente{color:#92400e;font-style:italic;}
        .obs{font-size:11px;color:#64748b;margin-top:8px;}
        @media print{body{padding:0;}}
      </style></head><body><div class="doc">
        <h1>${esc(termo.titulo)}</h1>
        ${avisoHtml}
        <pre>${esc(semAvisoLegado(termo.conteudo))}</pre>
        ${blocoAssinatura}
      </div></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  }

  async function abrirEditor(t: Termo) {
    let termo = t;
    try { const { data } = await api.get<Termo>(`/secretaria/termos/${t.id}`); if (data) termo = data; } catch { /* usa lista */ }
    setTextoEdit(semAvisoLegado(termo.conteudo));
    setEditandoTexto(termo);
  }

  async function salvarTexto() {
    if (!editandoTexto) return;
    await acao(() => api.put(`/secretaria/termos/${editandoTexto.id}`, { conteudo: textoEdit }), 'Texto do documento salvo.');
    setEditandoTexto(null);
  }

  async function aprovarModelo(t: Termo) {
    if (!window.confirm('Aprovar este modelo como o documento OFICIAL adotado pela creche? O aviso de "modelo" será removido e o documento poderá ser assinado.')) return;
    await acao(() => api.post(`/secretaria/termos/${t.id}/aprovar-modelo`), 'Modelo aprovado — pronto para assinatura.');
  }

  /**
   * Exclui um termo NÃO validado (modelo/pendente, recusado ou revogado). O
   * documento assinado e vigente não chega aqui — o botão fica desabilitado e o
   * backend recusa. Toda exclusão vira evento de auditoria.
   */
  async function excluir(t: Termo) {
    const ok = window.confirm(
      `Excluir "${t.titulo}" (situação: ${t.status})?\n\n` +
      'O documento sai da lista da creche. A exclusão fica registrada nos Eventos de Auditoria ' +
      '(quem excluiu, quando, de qual IP e qual documento).',
    );
    if (!ok) return;
    await acao(() => api.delete(`/secretaria/termos/${t.id}`), 'Termo excluído — registrado na auditoria.');
  }

  async function confirmarAssinatura() {
    if (!assinando) return;
    await acao(() => api.post(`/secretaria/termos/${assinando.id}/assinar`, assinatura), 'Termo assinado.');
    setAssinando(null); setAssinatura({ assinante_nome: '', assinante_cpf: '' });
  }

  if (loading) return <div style={{ padding: 24 }}>Carregando…</div>;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>Secretaria & Termos</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b' }}>Contratos e termos com assinatura digital e conformidade LGPD.</p>
      </div>

      <div style={{ ...card, padding: '12px 16px', borderLeft: '4px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 13 }}>
        ⚠️ <strong>Atenção:</strong> todos os modelos oferecidos pelo sistema (Contrato, Uso de Imagem, Saída Pedagógica, Autorização de Medicamentos e Consentimento LGPD) são apenas um <strong>ponto de partida</strong>. Sua adoção pela creche depende de <strong>aprovação prévia do departamento jurídico</strong> da instituição. Esse aviso também consta no topo de cada documento gerado.
      </div>

      {msg && (
        <div style={{ ...card, padding: '12px 16px', borderLeft: `4px solid ${msg.tipo === 'ok' ? EMERALD : '#ef4444'}`, color: msg.tipo === 'ok' ? '#065f46' : '#991b1b' }}>{msg.texto}</div>
      )}

      {/* Criar termo */}
      <section style={card}>
        <h2 style={{ fontSize: 16, marginTop: 0, color: '#0f172a' }}>Novo termo</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 12, alignItems: 'end' }}>
          <label style={{ fontSize: 13 }}>Criança
            <select style={inp} value={novo.id_aluno} onChange={(e) => setNovo({ ...novo, id_aluno: Number(e.target.value) })}>
              <option value={0}>Selecione…</option>
              {alunos.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Tipo
            <select style={inp} value={novo.tipo} onChange={(e) => setNovo({ ...novo, tipo: e.target.value })}>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 13 }}>Título {novo.tipo !== 'outro' && <span style={{ color: '#94a3b8' }}>(automático)</span>}
            <input style={inp} value={novo.titulo} onChange={(e) => setNovo({ ...novo, titulo: e.target.value })} placeholder={novo.tipo === 'outro' ? 'Informe o título do termo' : 'Definido pelo modelo'} disabled={novo.tipo !== 'outro'} />
          </label>
          <button style={btn(EMERALD)} onClick={criar}>{novo.tipo === 'outro' ? 'Criar' : 'Gerar modelo'}</button>
        </div>
      </section>

      {/* Lista */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, color: '#0f172a', margin: 0 }}>Termos</h2>
          <select style={{ ...inp, width: 200 }} value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="assinado">Assinado</option>
            <option value="revogado">Revogado</option>
          </select>
        </div>
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>Título</th>
                <th style={{ padding: 12 }}>Tipo</th>
                <th style={{ padding: 12 }}>Criança</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Assinante</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {termos.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#64748b' }}>Nenhum termo.</td></tr>
              ) : termos.map((t) => {
                const [bg, fg] = statusColor[t.status] || ['#f1f5f9', '#475569'];
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 12 }}>{t.titulo}</td>
                    <td style={{ padding: 12 }}>{labelTipo(t.tipo)}</td>
                    <td style={{ padding: 12 }}>{t.aluno?.nome || `#${t.id_aluno}`}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{ background: bg, color: fg, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{t.status}</span>
                      {t.status === 'pendente' && (
                        <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, ...(t.modelo_aprovado ? { background: '#dbeafe', color: '#1e40af' } : { background: '#fef3c7', color: '#92400e' }) }}>
                          {t.modelo_aprovado ? '✓ aprovado' : '⚠ modelo'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>{t.assinante_nome || '—'}</td>
                    <td style={{ padding: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn('#334155'), marginRight: 8 }} onClick={() => verDocumento(t)}>Ver / Imprimir</button>
                      {t.status === 'pendente' && !t.modelo_aprovado && (
                        <>
                          <button style={{ ...btn('#7c3aed'), marginRight: 8 }} onClick={() => abrirEditor(t)}>Editar texto</button>
                          <button style={{ ...btn('#2563eb'), marginRight: 8 }} onClick={() => aprovarModelo(t)}>Aprovar modelo</button>
                        </>
                      )}
                      {t.status === 'pendente' && t.modelo_aprovado && <button style={{ ...btn(EMERALD), marginRight: 8 }} onClick={() => abrirAssinatura(t)}>Assinar</button>}
                      {t.status !== 'revogado' && <button style={{ ...btn('#ef4444'), marginRight: 8 }} onClick={() => acao(() => api.post(`/secretaria/termos/${t.id}/revogar`), 'Termo revogado.')}>Revogar</button>}
                      {t.status === 'assinado' ? (
                        <button
                          style={{ ...btn('#cbd5e1'), color: '#64748b', cursor: 'not-allowed' }}
                          disabled
                          title="Documento assinado e validado não pode ser excluído. Se ele não vale mais, use Revogar."
                        >
                          Excluir
                        </button>
                      ) : (
                        <button style={btn('#b91c1c')} onClick={() => excluir(t)} title="Exclusão registrada nos Eventos de Auditoria">Excluir</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal editar texto do modelo */}
      {editandoTexto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setEditandoTexto(null)}>
          <div style={{ ...card, width: 760, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Editar texto — {editandoTexto.titulo}</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>Revise/adeque o texto do documento à realidade da creche. Depois clique em <strong>Aprovar modelo</strong> para adotá-lo como oficial.</p>
            <textarea value={textoEdit} onChange={(e) => setTextoEdit(e.target.value)} style={{ ...inp, minHeight: 380, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button style={btn('#94a3b8')} onClick={() => setEditandoTexto(null)}>Cancelar</button>
              <button style={btn(EMERALD)} onClick={salvarTexto}>Salvar texto</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal assinatura */}
      {assinando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setAssinando(null)}>
          <div style={{ ...card, width: 520, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Assinar: {assinando.titulo}</h3>
            {assinando.conteudo && (
              <pre style={{
                whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: 14, fontFamily: 'inherit', fontSize: 13,
                color: '#334155', maxHeight: 260, overflowY: 'auto', marginBottom: 16,
              }}>
                {assinando.conteudo}
              </pre>
            )}
            <label style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>Nome do assinante
              <input style={inp} value={assinatura.assinante_nome} onChange={(e) => setAssinatura({ ...assinatura, assinante_nome: e.target.value })} />
            </label>
            <label style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>CPF do assinante
              <input style={inp} value={assinatura.assinante_cpf} onChange={(e) => setAssinatura({ ...assinatura, assinante_cpf: formatCpf(e.target.value) })} placeholder="000.000.000-00" />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={btn('#94a3b8')} onClick={() => setAssinando(null)}>Cancelar</button>
              <button style={btn(EMERALD)} onClick={confirmarAssinatura}>Confirmar assinatura</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
